import React, { useCallback, useEffect, useRef, useState } from 'react';
import PageBanner from '../components/PageBanner';
import api from '../utils/api';

const PER_PAGE = 24;

const galleryStyles = {
  section: {
    padding: '60px 0 80px',
    background: '#fff',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '16px',
    color: 'var(--body-text)',
    maxWidth: '600px',
    margin: '0 auto 40px',
    lineHeight: 1.6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
  },
  imageWrap: {
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    aspectRatio: '4/3',
    position: 'relative',
    cursor: 'pointer',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    border: '1px solid var(--border-light)',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 0.4s ease',
  },
  status: {
    textAlign: 'center',
    padding: '30px 0 10px',
    color: 'var(--text-light)',
    fontSize: '14px',
  },
  sentinel: {
    height: '1px',
    width: '100%',
  },
};

// Video tile: same 4/3 crop as images, click to play/pause with sound.
function GalleryVideo({ src }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  return (
    <div
      className="gallery-video"
      onClick={toggle}
      role="button"
      tabIndex={0}
      aria-label={playing ? 'Pause video' : 'Play video'}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
    >
      <video
        ref={videoRef}
        src={src}
        style={galleryStyles.image}
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {!playing && (
        <span className="gallery-video__play" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="6 4 20 12 6 20" />
          </svg>
        </span>
      )}
    </div>
  );
}

function GalleryPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sentinelRef = useRef(null);
  // Refs mirror state so the IntersectionObserver callback (created once) always
  // reads current values without re-subscribing on every render.
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageRef = useRef(0);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    const next = pageRef.current + 1;
    try {
      const res = await api.getGallery(next, PER_PAGE);
      setItems((prev) => [...prev, ...(res.items || [])]);
      pageRef.current = next;
      setPage(next);
      hasMoreRef.current = !!res.has_more;
      setHasMore(!!res.has_more);
    } catch (e) {
      setError(e.message || 'Failed to load gallery');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <>
      <PageBanner title="Gallery" />

      <section style={galleryStyles.section}>
        <div className="container">
          <h2 className="section-title">Our Moments</h2>
          <div className="section-divider">
            <span className="line"></span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="line"></span>
          </div>
          <p style={galleryStyles.subtitle}>
            A glimpse into our sessions, events, and community engagements.
          </p>

          <div style={galleryStyles.grid} className="gallery-page-grid">
            {items.map((img, index) => (
              <div
                key={`${img.src}-${index}`}
                style={galleryStyles.imageWrap}
                className="gallery-page-item"
              >
                {img.type === 'video' ? (
                  <GalleryVideo src={img.src} />
                ) : (
                  <img
                    src={img.src}
                    alt={img.alt || 'MindSpa Gallery'}
                    style={galleryStyles.image}
                    loading="lazy"
                  />
                )}
              </div>
            ))}
          </div>

          <div ref={sentinelRef} style={galleryStyles.sentinel} aria-hidden="true" />

          {loading && (
            <div style={galleryStyles.status}>Loading more…</div>
          )}
          {!loading && !hasMore && items.length > 0 && (
            <div style={galleryStyles.status}>You've reached the end.</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div style={galleryStyles.status}>No images yet.</div>
          )}
          {error && (
            <div style={{ ...galleryStyles.status, color: '#c0392b' }}>{error}</div>
          )}
        </div>
      </section>

      <style>{`
        .gallery-page-item:hover {
          transform: scale(1.03);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }
        .gallery-page-item:hover img {
          transform: scale(1.05);
        }
        .gallery-video {
          width: 100%;
          height: 100%;
        }
        .gallery-video__play {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(30, 39, 40, 0.55);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }
        .gallery-video__play svg { margin-left: 3px; }
        @media (max-width: 900px) {
          .gallery-page-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 500px) {
          .gallery-page-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}

export default GalleryPage;
